/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { resources } from './api';
import {
    CustomersData, DataRest, HeadquartersData, IdDataRest, NextRatingDateData, NotificationMaintenanceData, PaginatedCustomers,
    PeopleCheckData, ReleaseNoteData, StringsData, UserData, UsersData
} from '../model/api/data-rest';
import { User } from '../model/user';
import { PaginationParams, toHttpParams } from '../model/api/pagination-params';
import { InvitationData } from '../model/api/invitation-data';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    constructor(
        private http: HttpClient,
    ) {
    }

    getUser(): Observable<UserData> {
        return this.http.get<UserData>(`${environment.apiUrl}${resources.get('users')}`);
    }

    getUserById(userId: number): Observable<UserData> {
        return this.http.get<UserData>(`${environment.apiUrl}${resources.get('users')}${resources.get('id')}/${userId}`);
    }

    updateUser(user: User): Observable<DataRest> {
        return this.http.put<DataRest>(`${environment.apiUrl}${resources.get('users')}/${user.Id}`, user);
    }

    getUserTechnicians(): Observable<UsersData> {
        return this.http.get<UsersData>(`${environment.apiUrl}${resources.get('users')}${resources.get('technicians')}`);
    }

    getHeadQuarters(): Observable<HeadquartersData> {
        return this.http.get<HeadquartersData>(`${environment.apiUrl}${resources.get('users')}${resources.get('headquarters')}`);
    }

    confirmShareDevice(invitationData: InvitationData): Observable<DataRest> {
        let params = new HttpParams()
            .append('deviceId', invitationData.DeviceId.toString())
            .append('ownerId', invitationData.OwnerId.toString())
            .append('remotesMax', invitationData.RemotesMax.toString())
            .append('verification', invitationData.Verification);
        if (invitationData.UserId) {
            params = params.append('userId', invitationData.UserId.toString());
            return this.http.post<DataRest>(
                `${environment.apiUrl}${resources.get('devices')}${resources.get('confirm')}${resources.get('share')}${resources.get('onSignup')}`,
                {},
                { params }
            );
        }
        return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('devices')}${resources.get('confirm')}${resources.get('share')}`, {}, { params });
    }

    confirmSiteTransfer(siteId: number, ownerId: number, verification: string, userId?: number): Observable<DataRest> {
        let params = new HttpParams()
            .set('siteId', siteId.toString())
            .set('ownerId', ownerId.toString())
            .set('verification', verification);
        if (userId) {
            params = params.set('userId', userId.toString());
            return this.http.post<DataRest>(
                `${environment.apiUrl}${resources.get('sites')}${resources.get('confirm')}${resources.get('transfer')}${resources.get('onSignup')}`,
                {},
                { params }
            );
        } else {
            return this.http.post<DataRest>(
                `${environment.apiUrl}${resources.get('sites')}${resources.get('confirm')}${resources.get('transfer')}`,
                {},
                { params }
            );
        }
    }

    getTechnicianByCode(code: string): Observable<UserData> {
        return this.http.get<UserData>(`${environment.apiUrl}${resources.get('users')}${resources.get('by')}${resources.get('technicianCode')}/${code}`);
    }

    assignTechnician(techCode?: string, userId?: number, technicianId?: number, verification?: string): Observable<DataRest> {
        let params: HttpParams = new HttpParams();
        if (techCode) {
            params = params.set('technicianCode', techCode);
            return this.http.post<DataRest>(
                `${environment.apiUrl}${resources.get('users')}${resources.get('technicians')}${resources.get('techCode')}`,
                {},
                { params }
            );
        }
        params = params.set('technicianId', technicianId.toString());
        params = params.set('verification', verification);
        if (userId) {
            params = params.set('userId', userId.toString());
            return this.http.post<DataRest>(
                `${environment.apiUrl}${resources.get('oauth')}${resources.get('technicians')}${resources.get('onSignup')}`,
                {},
                { params }
            );
        }
        return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('users')}${resources.get('technicians')}`, {}, { params });
    }

    unassignTechnician(id: number): Observable<DataRest> {
        const params: HttpParams = new HttpParams().append('technicianId', id.toString());
        return this.http.post<DataRest>(
            `${environment.apiUrl}${resources.get('users')}${resources.get('technicians')}${resources.get('unassign')}`,
            {},
            { params });
    }

    getFinalUsers(deviceId: number): Observable<UsersData> {
        return this.http.get<UsersData>(`${environment.apiUrl}${resources.get('users')}${resources.get('finalUsers')}${resources.get('byDevice')}/${deviceId}`);
    }

    revokeShareInvitation(email: string, deviceId: number): Observable<DataRest> {
        const params = new HttpParams()
            .append('email', email)
            .append('deviceId', deviceId.toString());
        return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('shareDevicePendingInvitations')}`, { params });
    }

    revokeCustomerInvitation(email: string): Observable<DataRest> {
        const params = new HttpParams()
            .append('email', email);
        return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('customerPendingInvitations')}`, { params });
    }

    deleteCustomer(userId: number): Observable<DataRest> {
        return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('users')}${resources.get('customers')}/${userId}`);
    }

    closeAccount(pwd: string): Observable<DataRest> {
        const payload = { password: pwd };
        return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('oauth')}${resources.get('delete')}${resources.get('user')}`, payload);
    }

    getCustomers(paging?: PaginationParams, filter?: string, status?: string): Observable<PaginatedCustomers> {
        let params: HttpParams;
        if (paging) {
            params = toHttpParams(paging);
        }
        if (filter) {
            params = params.append('filter', filter);
        }
        if (status) {
            params = params.append('status', status);
        }
        return this.http.get<PaginatedCustomers>(`${environment.apiUrl}${resources.get('users')}${resources.get('customers')}`, { params });
    }

    getAllCustomers(): Observable<CustomersData> {
        return this.http.get<CustomersData>(`${environment.apiUrl}${resources.get('users')}${resources.get('customers')}`);
    }

    inviteCustomer(email: string, firstName: string, lastName: string): Observable<DataRest> {
        const payload = {
            Email: email,
            FirstName: firstName,
            LastName: lastName
        };
        return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('users')}${resources.get('invitation')}`, payload);
    }

    inviteCustomerNew(email: string, firstName: string, lastName: string): Observable<DataRest> {
        const payload = {
            Email: email,
            FirstName: firstName,
            LastName: lastName
        };
        return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('invitations')}`, payload);
    }

    checkPeople(email: string) {
        return this.http.post<PeopleCheckData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('insights')}${resources.get('peopleCheck')}`, { email }, {});
    }

    searchEmail(email: string) {
        const params = new HttpParams()
            .append('email', email);
        return this.http.get<StringsData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('insights')}${resources.get('searchEmail')}`, { params });
    }

    getNotificationMaintenance(appid: string, language: string): Observable<NotificationMaintenanceData> {
        const params = new HttpParams()
            .append('appid', appid)
            .append('language', language);
        return this.http.get<NotificationMaintenanceData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('notifications')}`, { params });
    }

    setFePrecLogin(userId: number): Observable<DataRest> {
        return this.http.put<DataRest>(`${environment.apiUrl}${resources.get('users')}${resources.get('fepreclogin')}/${userId}`, {});
    }

    getReleaseNoteList(appid: string, language: string): Observable<ReleaseNoteData> {
        const params = new HttpParams()
            .append('appid', appid)
            .append('language', language);
        return this.http.get<ReleaseNoteData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('releasenotes')}`, { params });
    }

    setFeReleaseVersion(userId: number, version: string): Observable<DataRest> {
        const params = new HttpParams()
            .append('version', version);
        return this.http.put<DataRest>(`${environment.apiUrl}${resources.get('users')}${resources.get('fereleaseversion')}/${userId}`, {}, { params });
    }

    setFePrivacyVersion(userId: number, version: string): Observable<DataRest> {
        const params = new HttpParams()
            .append('version', version);
        return this.http.put<DataRest>(`${environment.apiUrl}${resources.get('users')}${resources.get('feprivacyversion')}/${userId}`, {}, { params });
    }

    setRatingVote(ratingValue: number, text: string, appVersion: string): Observable<IdDataRest> {
        const payload = {
            Rating: ratingValue,
            Text: text,
            AppVersion: appVersion
        };
        return this.http.post<IdDataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('rating')}`, payload);
    }

    getNextRatingVote(): Observable<NextRatingDateData> {
        return this.http.get<NextRatingDateData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('rating')}`);
    }

    deleteKeycode(keycode: string): Observable<DataRest> {
        const params = new HttpParams();
        return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('devices')}/${keycode}`, { params });
    }

    deleteUser(userId: number): Observable<DataRest> {
        const params = new HttpParams();
        return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('oauth')}${resources.get('delete')}${resources.get('user')}/${userId}`, { params });
    }

}
